/**
 * Browser capability helpers.
 *
 * - Badging API: unread count on the app/tab icon
 * - Web Share API: the platform share sheet
 * - Screen Wake Lock: keep the screen awake
 *
 * These are ordinary web APIs, not PWA machinery, which is why the file survived Card 30.
 * The `pwaInstall` helper that used to live here (standalone-mode / display-mode detection)
 * did NOT: the app is no longer installable, so there is no install state to report.
 */

/**
 * Badging API
 * Shows notification count badge on the app icon (when installed as PWA)
 */

export const badging = {
  /**
   * Check if Badging API is supported
   */
  isSupported(): boolean {
    return 'setAppBadge' in navigator;
  },

  /**
   * Set badge count on app icon
   * @param count - Number to display (0 to clear)
   */
  async set(count: number): Promise<void> {
    if (!this.isSupported()) {
      console.warn('[Badging] API not supported in this browser');
      return;
    }

    try {
      if (count === 0) {
        await this.clear();
      } else {
        await (navigator as any).setAppBadge(count);
        console.log(`[Badging] Set badge to ${count}`);
      }
    } catch (error) {
      console.error('[Badging] Failed to set badge:', error);
    }
  },

  /**
   * Clear the badge
   */
  async clear(): Promise<void> {
    if (!this.isSupported()) {
      return;
    }

    try {
      await (navigator as any).clearAppBadge();
      console.log('[Badging] Cleared badge');
    } catch (error) {
      console.error('[Badging] Failed to clear badge:', error);
    }
  },

  /**
   * Increment badge count
   */
  async increment(currentCount: number): Promise<void> {
    await this.set(currentCount + 1);
  },

  /**
   * Decrement badge count
   */
  async decrement(currentCount: number): Promise<void> {
    await this.set(Math.max(0, currentCount - 1));
  },
};

/**
 * Web Share API
 * Native sharing functionality for text, URLs, and files
 */

interface ShareData {
  title?: string;
  text?: string;
  url?: string;
  files?: File[];
}

export const webShare = {
  /**
   * Check if Web Share API is supported
   */
  isSupported(): boolean {
    return 'share' in navigator;
  },

  /**
   * Check if files can be shared
   */
  canShareFiles(): boolean {
    return 'canShare' in navigator && (navigator as any).canShare({ files: [] });
  },

  /**
   * Share content using native share dialog
   */
  async share(data: ShareData): Promise<boolean> {
    if (!this.isSupported()) {
      console.warn('[WebShare] API not supported in this browser');
      return false;
    }

    try {
      // Check if the data can be shared
      if ('canShare' in navigator) {
        const canShare = (navigator as any).canShare(data);
        if (!canShare) {
          console.warn('[WebShare] Cannot share this data');
          return false;
        }
      }

      await navigator.share(data);
      console.log('[WebShare] Share successful');
      return true;
    } catch (error: any) {
      // User cancelled share - not an error
      if (error.name === 'AbortError') {
        console.log('[WebShare] Share cancelled by user');
        return false;
      }

      console.error('[WebShare] Share failed:', error);
      return false;
    }
  },

  /**
   * Share a URL with optional title and text
   */
  async shareUrl(url: string, title?: string, text?: string): Promise<boolean> {
    return this.share({ url, title, text });
  },

  /**
   * Share text content
   */
  async shareText(text: string, title?: string): Promise<boolean> {
    return this.share({ text, title });
  },

  /**
   * Share files (images, documents, etc.)
   */
  async shareFiles(files: File[], title?: string, text?: string): Promise<boolean> {
    if (!this.canShareFiles()) {
      console.warn('[WebShare] File sharing not supported');
      return false;
    }

    return this.share({ files, title, text });
  },

  /**
   * Fallback: Copy to clipboard if share not supported
   */
  async copyToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      console.log('[WebShare] Copied to clipboard as fallback');
      return true;
    } catch (error) {
      console.error('[WebShare] Failed to copy to clipboard:', error);
      return false;
    }
  },

  /**
   * Share or copy to clipboard (with automatic fallback)
   */
  async shareOrCopy(data: ShareData): Promise<boolean> {
    if (this.isSupported()) {
      return this.share(data);
    }

    // Fallback to clipboard for text/URL
    const textToCopy = data.url || data.text || '';
    if (textToCopy) {
      return this.copyToClipboard(textToCopy);
    }

    return false;
  },
};

/**
 * Screen Wake Lock API
 * Prevent screen from going to sleep during specific activities
 */

export const wakeLock = {
  lock: null as WakeLockSentinel | null,

  /**
   * Check if Wake Lock API is supported
   */
  isSupported(): boolean {
    return 'wakeLock' in navigator;
  },

  /**
   * Request a wake lock to keep screen on
   */
  async request(): Promise<boolean> {
    if (!this.isSupported()) {
      console.warn('[WakeLock] API not supported in this browser');
      return false;
    }

    try {
      this.lock = await (navigator as any).wakeLock.request('screen');
      console.log('[WakeLock] Screen wake lock acquired');

      // Re-request lock when page becomes visible again
      document.addEventListener('visibilitychange', async () => {
        if (document.visibilityState === 'visible' && this.lock) {
          this.lock = await (navigator as any).wakeLock.request('screen');
          console.log('[WakeLock] Wake lock re-acquired after visibility change');
        }
      });

      return true;
    } catch (error) {
      console.error('[WakeLock] Failed to acquire wake lock:', error);
      return false;
    }
  },

  /**
   * Release the wake lock
   */
  async release(): Promise<void> {
    if (this.lock) {
      await this.lock.release();
      this.lock = null;
      console.log('[WakeLock] Screen wake lock released');
    }
  },

  /**
   * Check if wake lock is currently active
   */
  isActive(): boolean {
    return this.lock !== null && !this.lock.released;
  },
};

/**
 * Aggregate capability check
 */

export interface PWACapabilities {
  badging: boolean;
  webShare: boolean;
  webShareFiles: boolean;
  wakeLock: boolean;
}

export function checkPWACapabilities(): PWACapabilities {
  return {
    badging: badging.isSupported(),
    webShare: webShare.isSupported(),
    webShareFiles: webShare.canShareFiles(),
    wakeLock: wakeLock.isSupported(),
  };
}
